import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createInboxClient,
  InboxApiError,
  inboxAvailable,
  materialOf,
  sourceLink,
} from '../api/inbox.js';
import type { InboxClient, InboxItem, InboxSession, InboxSummary, Material } from '../api/inbox.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import '../styles/inbox.css';

const sourceNames = {
  local: '로컬 파일',
  slack: 'Slack',
  notion: 'Notion',
  drive: 'Google Drive',
  user_context: '업무 맥락',
  other: '기타',
};
const representationNames = {
  original: '원문 그대로',
  excerpt: '원문 일부 발췌',
  user_statement: '본인이 설명한 내용',
  assistant_summary: 'AI가 정리한 내용',
};
const visibilityNames = {
  company: '사내 공개',
  limited: '일부 사람만 접근',
  only_me: '나만 접근',
  unknown: '확인 전',
};
const historyNames: Record<string, string> = {
  submit: '접수',
  update: '내용 수정',
  upload: '파일 업로드',
  delete: '삭제',
  expire: '보존 만료',
};
const date = (n: number) =>
  new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeZone: 'Asia/Seoul' }).format(
    n * 1000,
  );
const status = (item: InboxSummary) =>
  item.state === 'awaiting_file' ? '파일 업로드 미완료' : '검수 전 보관';

function errorText(e: unknown): string {
  if (!(e instanceof InboxApiError)) return '처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  if (e.status === 409)
    return '다른 곳에서 이 자료가 변경됐습니다. 최신 내용을 다시 불러온 뒤 수정해 주세요.';
  if (e.status === 404)
    return '삭제되었거나 보존 기간이 끝난 자료입니다. 목록을 새로고침해 주세요.';
  if (e.status === 401) return '로그인이 만료됐습니다. 소피 계정으로 다시 연결해 주세요.';
  if (e.status === 503)
    return '수신함이 아직 개통되지 않았거나 일시적으로 사용할 수 없습니다. AX실에 확인해 주세요.';
  if (e.status === 0)
    return '수신함 서버에 연결하지 못했습니다. 네트워크와 개통 상태를 확인해 주세요.';
  if (e.code === 'file_hash_mismatch')
    return '등록된 파일과 내용이 다릅니다. 처음 제출한 파일을 선택해 주세요.';
  if (/sensitive|sharing_check|credential/.test(e.code))
    return '민감정보 또는 공유 가능 여부를 확인해 주세요. 개인·인사·급여·인증정보는 제출할 수 없습니다.';
  if (e.status === 429)
    return '현재 저장 또는 요청 한도에 도달했습니다. 잠시 후 다시 시도해 주세요.';
  return '요청을 처리하지 못했습니다. 내용을 확인하고 다시 시도해 주세요.';
}

export function InboxPage({
  client: provided,
  demo = false,
}: {
  client?: InboxClient;
  demo?: boolean;
}): JSX.Element {
  const [client] = useState(() => provided ?? createInboxClient());
  const [session, setSession] = useState<InboxSession | null>(null);
  const [items, setItems] = useState<InboxSummary[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [selected, setSelected] = useState<InboxItem | null>(null);
  const [draft, setDraft] = useState<Material | null>(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const generation = useRef(0);
  useDocumentMeta({ title: '내 수신함 · Company Brain', noindex: true });

  function fail(e: unknown) {
    setError(errorText(e));
    if (e instanceof InboxApiError && e.status === 401) {
      generation.current++;
      setSession(null);
      setItems([]);
      setSelected(null);
      setDraft(null);
      setEditing(false);
      setDeleting(false);
    }
  }
  async function refresh(append = false) {
    const result = await client.list(append ? next || '' : '');
    setItems((old) =>
      append
        ? [...old, ...result.items.filter((r) => !old.some((o) => o.item_id === r.item_id))]
        : result.items,
    );
    setNext(result.next_before_id);
  }
  async function connect(initial = false) {
    setBusy(true);
    setError('');
    try {
      setSession(await client.session());
      await refresh();
    } catch (e) {
      fail(e);
      // A first visit has no inbox session yet; this is not an expired login.
      if (initial && e instanceof InboxApiError && e.status === 401) setError('');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (inboxAvailable()) void connect(true);
    else setBusy(false);
  }, [client]);

  async function open(item: InboxSummary) {
    if (editing && !window.confirm('저장하지 않은 수정을 버리고 이동할까요?')) return;
    const current = ++generation.current;
    setBusy(true);
    setError('');
    setNotice('');
    setSelected(null);
    setDraft(null);
    setEditing(false);
    setDeleting(false);
    try {
      const value = await client.get(item.item_id);
      if (current !== generation.current) return;
      if (value.state === 'deleted') throw new InboxApiError(404, 'submission_not_found');
      setSelected(value);
      setDraft(materialOf(value));
    } catch (e) {
      if (current === generation.current) fail(e);
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  async function mutate(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!selected || !draft) return;
    await mutate(async () => {
      const updated = await client.update(selected, draft);
      setSelected(updated);
      setDraft(materialOf(updated));
      setEditing(false);
      setNotice('수정했습니다. Company Brain 검색에는 아직 반영되지 않습니다.');
      await refresh();
    });
  }
  async function download() {
    if (!selected) return;
    await mutate(async () => {
      const blob = await client.download(selected);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selected.file_name.replace(/[/\\\x00-\x1f]/g, '_');
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  if (!inboxAvailable())
    return (
      <main>
        <h1>이 주소에서는 수신함을 사용할 수 없습니다.</h1>
        <Link to="/documents">자료실로 이동</Link>
      </main>
    );
  return (
    <div className="cb-inbox">
      <header className="cb-top">
        <Link className="cb-wordmark" to="/inbox">
          <span aria-hidden="true">s.</span> Sweetspot <small>Company Brain</small>
        </Link>
        <nav aria-label="자료 메뉴">
          <Link to="/inbox" aria-current="page">
            내 수신함
          </Link>
          <Link to="/documents">업무 자료 ↗</Link>
        </nav>
        {session && (
          <div className="cb-user">
            <span>{session.user.name || session.user.email}</span>
            <button
              disabled={busy}
              onClick={() =>
                void mutate(async () => {
                  await client.logout();
                  generation.current++;
                  setSession(null);
                  setItems([]);
                  setSelected(null);
                  setDraft(null);
                  setEditing(false);
                  setDeleting(false);
                })
              }
            >
              연결 해제
            </button>
          </div>
        )}
      </header>
      <main className="cb-main">
        {demo && (
          <div className="cb-demo">
            로컬 시연 · 아래 자료는 가상 예시입니다. 변경은 이 화면에서만 유지되며 서버로 전송되지
            않습니다.
          </div>
        )}
        <div className="cb-heading">
          <div>
            <div className="cb-eyebrow">MY CONTRIBUTIONS</div>
            <h1>내가 보낸 자료</h1>
            <p>Claude와 일하며 Company Brain에 보낸 파일과 업무 맥락을 확인하세요.</p>
          </div>
          <span className="cb-private">본인 자료만 표시</span>
        </div>
        <div className="cb-policy">
          <span aria-hidden="true">ⓘ</span>
          <div>
            <strong>접수와 검색 반영은 별도입니다.</strong> 지금은 자료를 검수 전 상태로 보관합니다.
            자동으로 공개하거나 답변에 사용하지 않습니다.
          </div>
        </div>
        {error && (
          <div role="alert" className="cb-error">
            {error}{' '}
            {!session && (
              <button onClick={() => void connect()} disabled={busy}>
                다시 확인
              </button>
            )}
          </div>
        )}
        {notice && (
          <p role="status" className="cb-notice">
            {notice}
          </p>
        )}
        {!session ? (
          <section className="cb-connect">
            <div className="cb-connect-icon" aria-hidden="true">
              ↳
            </div>
            <h2>{busy ? '수신함 확인 중' : '소피 계정으로 내 자료 확인'}</h2>
            <p>
              MCP와 같은 회사 계정으로 연결합니다.
              <br />
              기존 문서 공유 서비스의 로그인과는 별도입니다.
            </p>
            {!busy && (
              <a className="cb-primary" href={client.loginUrl}>
                소피 계정으로 연결 →
              </a>
            )}
            {new URLSearchParams(window.location.search).get('login') === 'failed' && (
              <p role="alert">
                로그인 요청이 만료되었거나 확인되지 않았습니다. 다시 연결해 주세요.
              </p>
            )}
          </section>
        ) : (
          <div className="cb-workspace" aria-busy={busy}>
            <aside className="cb-list-panel" aria-label="제출 자료 목록">
              <div className="cb-list-heading">
                <h2>
                  제출 자료{' '}
                  <span>
                    {items.length}
                    {next ? '+' : ''}
                  </span>
                </h2>
                <button
                  disabled={busy || editing}
                  onClick={() =>
                    void mutate(async () => {
                      setSelected(null);
                      setDraft(null);
                      await refresh();
                    })
                  }
                >
                  새로고침
                </button>
              </div>
              <input
                className="cb-search"
                type="search"
                aria-label="불러온 자료 검색"
                placeholder="불러온 자료에서 제목·설명 찾기"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {items.length === 0 && !busy && (
                <div className="cb-list-empty">
                  <strong>아직 보낸 자료가 없습니다.</strong>
                  <p>
                    Claude에게 “이 업무 맥락을 Company Brain 수신함에 보내줘”라고 요청해 보세요.
                    개인·인사·급여정보는 보내지 마세요.
                  </p>
                </div>
              )}
              <ul>
                {items
                  .filter((r) =>
                    `${r.title} ${r.description}`.toLowerCase().includes(filter.toLowerCase()),
                  )
                  .map((item) => (
                    <li key={item.item_id}>
                      <button
                        disabled={busy}
                        className={`cb-item ${selected?.item_id === item.item_id ? 'is-selected' : ''}`}
                        onClick={() => void open(item)}
                      >
                        <span className="cb-item-type">
                          {item.kind === 'file' ? '파일' : '업무 맥락'}
                          <span
                            className={item.state === 'awaiting_file' ? 'cb-dot warning' : 'cb-dot'}
                          />
                          {status(item)}
                        </span>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                        <small>
                          {date(item.expires_at)}까지 보관 <span>→</span>
                        </small>
                      </button>
                    </li>
                  ))}
              </ul>
              {filter &&
                items.length > 0 &&
                !items.some((r) =>
                  `${r.title} ${r.description}`.toLowerCase().includes(filter.toLowerCase()),
                ) && <p className="cb-list-empty">불러온 자료에 일치하는 항목이 없습니다.</p>}
              {next && (
                <button
                  className="cb-more"
                  disabled={busy}
                  onClick={() => void mutate(() => refresh(true))}
                >
                  이전 자료 더 보기
                </button>
              )}
            </aside>
            <section className="cb-detail" aria-label="제출 자료 상세">
              {!selected || !draft ? (
                <div className="cb-detail-empty">
                  <div aria-hidden="true">↖</div>
                  <h2>{busy ? '자료를 불러오는 중입니다.' : '확인할 자료를 선택하세요.'}</h2>
                  <p>
                    무엇을 보냈는지, 어떤 설명이 붙었는지 확인하고
                    <br />
                    잘못된 내용은 직접 수정할 수 있습니다.
                  </p>
                </div>
              ) : (
                <>
                  <div className="cb-detail-top">
                    <span className="cb-state">{status(selected)}</span>
                    <span>
                      v{selected.version} · {date(selected.created_at)} 접수
                    </span>
                  </div>
                  {editing ? (
                    <label className="cb-field">
                      제목
                      <input
                        value={draft.title}
                        maxLength={200}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      />
                    </label>
                  ) : (
                    <h2 className="cb-title">{selected.title}</h2>
                  )}
                  <div className="cb-attribution">
                    <strong>{selected.submitted_by.name || selected.submitted_by.email}</strong> ·{' '}
                    {selected.submitted_by.org_code || '본부 미확인'}
                    <br />
                    {selected.submitted_by.email}
                  </div>
                  <div className="cb-section">
                    <h3>이 자료를 보낸 이유</h3>
                    {editing ? (
                      <textarea
                        aria-label="자료 설명"
                        rows={3}
                        maxLength={2000}
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      />
                    ) : (
                      <p className="cb-prewrap">{selected.description}</p>
                    )}
                  </div>
                  <div className="cb-section">
                    <h3>{selected.kind === 'context' ? '보낸 내용' : '첨부 파일'}</h3>
                    {selected.kind === 'context' ? (
                      editing ? (
                        <textarea
                          aria-label="업무 맥락 본문"
                          rows={8}
                          value={draft.content_text}
                          maxLength={65536}
                          onChange={(e) => setDraft({ ...draft, content_text: e.target.value })}
                        />
                      ) : (
                        <div className="cb-content">{selected.content_text}</div>
                      )
                    ) : (
                      <div className="cb-file">
                        <div>
                          <strong>{selected.file_name}</strong>
                          <small>
                            {(selected.file_size / 1024).toFixed(1)} KB · 미리보기 없이 원본
                            다운로드
                          </small>
                        </div>
                        {selected.state === 'quarantined' ? (
                          <button disabled={busy || editing} onClick={() => void download()}>
                            내려받기 ↓
                          </button>
                        ) : (
                          <label className="cb-file-pick">
                            파일 업로드
                            <input
                              aria-label="미완료 파일 업로드"
                              disabled={busy || editing}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file)
                                  void mutate(async () => {
                                    await client.upload(selected, file);
                                    setSelected(await client.get(selected.item_id));
                                    await refresh();
                                  });
                              }}
                            />
                          </label>
                        )}
                      </div>
                    )}
                    {selected.state === 'awaiting_file' && (
                      <p className="cb-hint">
                        파일 정보만 접수되었습니다. 처음 등록한 파일과 크기·내용이 같은 파일을 올려
                        주세요.
                      </p>
                    )}
                  </div>
                  <div className="cb-section">
                    <h3>출처와 작성 방식</h3>
                    <dl className="cb-metadata">
                      <dt>자료가 있던 곳</dt>
                      <dd>
                        {editing ? (
                          <select
                            aria-label="자료 출처 종류"
                            value={draft.source_kind}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                source_kind: e.target.value as Material['source_kind'],
                              })
                            }
                          >
                            {Object.entries(sourceNames).map(([v, label]) => (
                              <option value={v} key={v}>
                                {label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          sourceNames[selected.source_kind]
                        )}
                      </dd>
                      <dt>원문 위치</dt>
                      <dd>
                        {editing ? (
                          <input
                            aria-label="원문 위치"
                            value={draft.source_locator}
                            maxLength={2000}
                            onChange={(e) => setDraft({ ...draft, source_locator: e.target.value })}
                          />
                        ) : sourceLink(selected.source_locator) ? (
                          <a
                            href={sourceLink(selected.source_locator)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            referrerPolicy="no-referrer"
                          >
                            {selected.source_locator} ↗
                          </a>
                        ) : (
                          selected.source_locator || '기록되지 않음'
                        )}
                      </dd>
                      <dt>작성 방식</dt>
                      <dd>
                        {editing ? (
                          <select
                            aria-label="작성 방식"
                            value={draft.representation}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                representation: e.target.value as Material['representation'],
                              })
                            }
                          >
                            {Object.entries(representationNames).map(([v, label]) => (
                              <option value={v} key={v}>
                                {label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          representationNames[selected.representation]
                        )}
                      </dd>
                      <dt>기존 접근 범위</dt>
                      <dd>
                        {editing ? (
                          <select
                            aria-label="기존 접근 범위"
                            value={draft.source_visibility}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                source_visibility: e.target.value as Material['source_visibility'],
                              })
                            }
                          >
                            {Object.entries(visibilityNames).map(([v, label]) => (
                              <option value={v} key={v}>
                                {label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          visibilityNames[selected.source_visibility]
                        )}
                        <small>제출 시 기록한 정보이며, 원문의 권한을 변경하지 않습니다.</small>
                      </dd>
                      <dt>보존 기한</dt>
                      <dd>
                        {date(selected.expires_at)}
                        <small>수정하거나 파일을 올려도 연장되지 않습니다.</small>
                      </dd>
                    </dl>
                  </div>
                  <details className="cb-history">
                    <summary>변경 이력 · {selected.history.length}건</summary>
                    <ol>
                      {selected.history.map((h, i) => (
                        <li key={i}>
                          {date(h.created_at)} · {historyNames[h.operation] || h.operation}{' '}
                          <span>v{h.version}</span>
                        </li>
                      ))}
                    </ol>
                    <p>변경 작업만 기록합니다. 이전 본문을 복원하는 기능은 없습니다.</p>
                    <small>제출 ID: {selected.item_id}</small>
                  </details>
                  {deleting ? (
                    <div className="cb-delete-confirm" role="group" aria-label="삭제 확인">
                      <strong>이 자료를 수신함에서 삭제할까요?</strong>
                      <p>
                        수신함의 내용과 파일을 제거합니다. Slack·Notion·로컬의 원본과 다른 사람의
                        제출물은 유지됩니다. 이 화면에서 복원할 수 없습니다.
                      </p>
                      <button
                        className="cb-danger"
                        disabled={busy}
                        onClick={() =>
                          void mutate(async () => {
                            await client.remove(selected);
                            setSelected(null);
                            setDraft(null);
                            setDeleting(false);
                            setNotice('수신함 사본을 삭제했습니다. 원본은 유지됩니다.');
                            await refresh();
                          })
                        }
                      >
                        수신함 사본 삭제
                      </button>
                      <button disabled={busy} onClick={() => setDeleting(false)}>
                        취소
                      </button>
                    </div>
                  ) : (
                    <div className="cb-actions">
                      {editing ? (
                        <>
                          <button
                            className="cb-primary"
                            disabled={
                              busy ||
                              !draft.title.trim() ||
                              !draft.description.trim() ||
                              (draft.kind === 'context' && !draft.content_text.trim())
                            }
                            onClick={() => void save()}
                          >
                            수정 저장
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => {
                              setDraft(materialOf(selected));
                              setEditing(false);
                              setError('');
                            }}
                          >
                            취소
                          </button>
                          <button disabled={busy} onClick={() => void open(selected)}>
                            최신 내용 다시 불러오기
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="cb-primary"
                            disabled={busy}
                            onClick={() => setEditing(true)}
                          >
                            내용 수정
                          </button>
                          <button
                            className="cb-danger-text"
                            disabled={busy}
                            onClick={() => setDeleting(true)}
                          >
                            삭제
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
        <footer className="cb-foot">
          Company Brain 수신함 · 개인·인사·급여정보와 인증정보는 보관 대상에서 제외합니다.
        </footer>
      </main>
    </div>
  );
}

export function InboxRoute(): JSX.Element {
  const [demoClient, setDemoClient] = useState<InboxClient | null>(null);
  const demo =
    import.meta.env.DEV &&
    inboxAvailable() &&
    new URLSearchParams(window.location.search).get('demo') === '1';
  useEffect(() => {
    if (demo) void import('../api/inbox-demo.js').then((m) => setDemoClient(m.createDemoInbox()));
  }, [demo]);
  return demo ? (
    demoClient ? (
      <InboxPage client={demoClient} demo />
    ) : (
      <main>로컬 시연 준비 중</main>
    )
  ) : (
    <InboxPage />
  );
}
